import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, LogOut, Lock, Package, Save, ChevronDown, ChevronRight, Trash2, CheckSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient, fetchProduct } from "../lib/api";
import { toast } from "sonner";

const TOKEN_KEY = "mc_admin_token";

const STATUS_OPTIONS = [
  { value: "confirmed", label: "Confirmed" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
  { value: "return", label: "Return" },
  { value: "refund_initiated", label: "Refund initiated" },
  { value: "refunded", label: "Refunded" },
];

const STATUS_COLORS = {
  placed: "bg-[#FFF5E1] text-[#F39C12]",
  confirmed: "bg-blue-50 text-blue-700",
  processing: "bg-blue-50 text-blue-700",
  shipped: "bg-indigo-50 text-indigo-700",
  delivered: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
  return: "bg-orange-50 text-orange-700",
  refund_initiated: "bg-amber-50 text-amber-700",
  refunded: "bg-emerald-50 text-emerald-700",
};

const InventoryPanel = ({ token }) => {
  const [product, setProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ stock: "", price: "", mrp: "" });

  const load = async () => {
    try {
      const p = await fetchProduct();
      setProduct(p);
      setForm({
        stock: String(p.stock ?? ""),
        price: String(p.price ?? ""),
        mrp: String(p.mrp ?? ""),
      });
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      const stockNum = parseInt(form.stock, 10);
      const priceNum = parseFloat(form.price);
      const mrpNum = parseFloat(form.mrp);
      if (!Number.isNaN(stockNum)) payload.stock = stockNum;
      if (!Number.isNaN(priceNum)) payload.price = priceNum;
      if (!Number.isNaN(mrpNum)) payload.mrp = mrpNum;
      const { data } = await apiClient.patch("/admin/product", payload, {
        headers: { "X-Admin-Token": token },
      });
      setProduct(data);
      setForm({
        stock: String(data.stock ?? ""),
        price: String(data.price ?? ""),
        mrp: String(data.mrp ?? ""),
      });
      toast.success("Inventory updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (!product) {
    return (
      <div className="mt-8 bg-white border border-[#F39C12]/20 rounded-2xl p-8 text-center text-[#7F8C8D]">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }

  const lowStock = (product.stock ?? 0) <= 10;

  return (
    <section
      data-testid="admin-inventory-panel"
      className="mt-8 bg-white border border-[#F39C12]/20 rounded-2xl p-5 sm:p-6"
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-5 h-5 text-[#D63031]" />
            <h2 className="font-display text-xl font-bold text-[#2C3E50]">
              Inventory · {product.name}
            </h2>
          </div>
          <p className="text-sm text-[#7F8C8D]">
            Current stock:{" "}
            <span
              data-testid="admin-current-stock"
              className={`font-semibold ${
                lowStock ? "text-[#D63031]" : "text-[#27AE60]"
              }`}
            >
              {product.stock} units
            </span>
            {lowStock && (
              <span className="ml-2 text-xs bg-[#D63031]/10 text-[#D63031] px-2 py-0.5 rounded-full font-medium">
                Low stock
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="stock">Stock (units)</Label>
          <Input
            id="stock"
            data-testid="admin-stock-input"
            type="number"
            min="0"
            value={form.stock}
            onChange={(e) => setForm({ ...form, stock: e.target.value })}
            className="mt-1 h-11"
          />
        </div>
        <div>
          <Label htmlFor="price">Selling price (₹)</Label>
          <Input
            id="price"
            data-testid="admin-price-input"
            type="number"
            min="1"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            className="mt-1 h-11"
          />
        </div>
        <div>
          <Label htmlFor="mrp">MRP (₹)</Label>
          <Input
            id="mrp"
            data-testid="admin-mrp-input"
            type="number"
            min="1"
            value={form.mrp}
            onChange={(e) => setForm({ ...form, mrp: e.target.value })}
            className="mt-1 h-11"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          data-testid="admin-inventory-save"
          onClick={save}
          disabled={saving}
          className="cta-red rounded-full h-11 px-6"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save changes
        </Button>
        <span className="text-xs text-[#7F8C8D]">
          Updates the live storefront immediately.
        </span>
      </div>
    </section>
  );
};


const AdminLogin = ({ onLogin }) => {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post("/admin/login", { pin });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="bg-white border border-[#F39C12]/20 rounded-2xl p-8 max-w-sm w-full text-center"
      >
        <div className="w-12 h-12 mx-auto rounded-full bg-[#FFF5E1] flex items-center justify-center mb-4">
          <Lock className="w-5 h-5 text-[#D63031]" />
        </div>
        <h1 className="font-display text-2xl font-bold text-[#2C3E50]">
          Admin sign-in
        </h1>
        <p className="text-sm text-[#7F8C8D] mt-1">
          Enter the admin PIN to access orders.
        </p>

        <div className="mt-6 text-left">
          <Label htmlFor="pin">PIN</Label>
          <Input
            id="pin"
            data-testid="admin-pin-input"
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Enter admin PIN"
            className="mt-1 h-11"
          />
        </div>

        <Button
          type="submit"
          data-testid="admin-login-btn"
          disabled={loading || !pin}
          className="cta-red rounded-full h-12 w-full mt-5 font-semibold"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
};

const ALL_STATUSES = [
  "placed",
  "confirmed",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "return",
  "refund_initiated",
  "refunded",
];

const STATUS_LABELS = {
  placed: "Placed",
  confirmed: "Confirmed",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  return: "Return",
  refund_initiated: "Refund initiated",
  refunded: "Refunded",
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [filterStatus, setFilterStatus] = useState("all");
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const authHeaders = token ? { "X-Admin-Token": token } : undefined;

  const statusCounts = useMemo(() => {
    const c = { all: orders.length };
    ALL_STATUSES.forEach((s) => (c[s] = 0));
    orders.forEach((o) => {
      const key = (o.status || "placed").toLowerCase();
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (filterStatus === "all") return orders;
    return orders.filter(
      (o) => (o.status || "").toLowerCase() === filterStatus
    );
  }, [orders, filterStatus]);

  const allFilteredSelected =
    filteredOrders.length > 0 &&
    filteredOrders.every((o) => selectedIds.has(o.order_number));

  const toggleRow = (orderNumber) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredOrders.forEach((o) => next.delete(o.order_number));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredOrders.forEach((o) => next.add(o.order_number));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const load = async (currentToken = token) => {
    if (!currentToken) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get("/orders", {
        headers: { "X-Admin-Token": currentToken },
      });
      setOrders(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Failed to load orders");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      // Verify token still valid
      apiClient
        .get("/admin/me", { headers: { "X-Admin-Token": token } })
        .then(() => load(token))
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        });
    }
  }, [token]);

  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = async () => {
    try {
      await apiClient.post(
        "/admin/logout",
        {},
        { headers: { "X-Admin-Token": token } }
      );
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setOrders([]);
  };

  const updateStatus = async (orderNumber, newStatus) => {
    setUpdatingId(orderNumber);
    try {
      const { data } = await apiClient.patch(
        `/orders/${orderNumber}/status`,
        { status: newStatus },
        { headers: authHeaders }
      );
      setOrders((prev) =>
        prev.map((o) => (o.order_number === orderNumber ? data : o))
      );
      toast.success(`Order ${orderNumber} → ${newStatus}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const bulkUpdateStatus = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const orderNumbers = Array.from(selectedIds);
      const { data } = await apiClient.patch(
        `/admin/orders/bulk-status`,
        { order_numbers: orderNumbers, status: bulkStatus },
        { headers: authHeaders }
      );
      setOrders((prev) =>
        prev.map((o) =>
          selectedIds.has(o.order_number) ? { ...o, status: bulkStatus } : o
        )
      );
      toast.success(`${data.modified} order(s) → ${bulkStatus}`);
      clearSelection();
      setBulkStatus("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const orderNumbers = Array.from(selectedIds);
      const { data } = await apiClient.post(
        `/admin/orders/bulk-delete`,
        { order_numbers: orderNumbers },
        { headers: authHeaders }
      );
      setOrders((prev) =>
        prev.filter((o) => !selectedIds.has(o.order_number))
      );
      toast.success(`${data.deleted} order(s) deleted`);
      clearSelection();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  if (!token) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div className="font-body py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#D63031] font-semibold">
              Admin
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">
              Dashboard
            </h1>
            <p className="text-sm text-[#7F8C8D] mt-1">
              Orders and inventory. Changes save instantly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh"
              onClick={() => load()}
              variant="outline"
              className="rounded-full h-11 px-5 border-[#F39C12]/40"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              data-testid="admin-logout"
              onClick={handleLogout}
              variant="outline"
              className="rounded-full h-11 px-5 border-[#D63031]/40 text-[#D63031]"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        <InventoryPanel token={token} />

        {/* Status dashboard */}
        <section
          data-testid="admin-status-dashboard"
          className="mt-8"
        >
          <h2 className="font-display text-lg font-semibold text-[#2C3E50] mb-3">
            Orders by status
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <button
              data-testid="status-card-all"
              onClick={() => setFilterStatus("all")}
              className={`text-left rounded-2xl border p-4 transition ${
                filterStatus === "all"
                  ? "border-[#D63031] bg-white shadow-sm"
                  : "border-[#F39C12]/20 bg-white/60 hover:bg-white"
              }`}
            >
              <div className="text-xs uppercase tracking-widest text-[#7F8C8D] font-semibold">
                All orders
              </div>
              <div className="mt-1 font-display text-2xl font-bold text-[#2C3E50]">
                {statusCounts.all || 0}
              </div>
            </button>
            {ALL_STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`status-card-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`text-left rounded-2xl border p-4 transition ${
                  filterStatus === s
                    ? "border-[#D63031] bg-white shadow-sm"
                    : "border-[#F39C12]/20 bg-white/60 hover:bg-white"
                }`}
              >
                <div
                  className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                    STATUS_COLORS[s] || STATUS_COLORS.placed
                  }`}
                >
                  {STATUS_LABELS[s]}
                </div>
                <div className="mt-2 font-display text-2xl font-bold text-[#2C3E50]">
                  {statusCounts[s] || 0}
                </div>
              </button>
            ))}
          </div>
        </section>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-2xl font-bold">
            Orders ({filteredOrders.length}
            {filterStatus !== "all" ? ` · ${STATUS_LABELS[filterStatus]}` : ""})
          </h2>
          {filterStatus !== "all" && (
            <button
              onClick={() => setFilterStatus("all")}
              data-testid="clear-status-filter"
              className="text-sm text-[#D63031] font-semibold hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div
            data-testid="admin-bulk-bar"
            className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl bg-[#2C3E50] text-white px-4 py-3"
          >
            <span
              data-testid="admin-bulk-count"
              className="font-semibold text-sm"
            >
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger
                  data-testid="admin-bulk-status-select"
                  className="h-9 w-44 bg-white text-[#2C3E50] border-0"
                >
                  <SelectValue placeholder="Change status to…" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="admin-bulk-status-apply"
                onClick={bulkUpdateStatus}
                disabled={!bulkStatus || bulkBusy}
                className="h-9 rounded-full bg-[#F39C12] text-[#2C3E50] hover:bg-[#e0900a]"
              >
                {bulkBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <CheckSquare className="w-4 h-4 mr-1" />
                )}
                Apply
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="admin-bulk-delete-btn"
                    variant="outline"
                    className="h-9 rounded-full bg-transparent border-white/40 text-white hover:bg-white hover:text-[#D63031]"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete {selectedIds.size} order(s)?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the selected orders from the
                      database. This cannot be undone. Google Sheet rows will
                      remain unless you delete them there.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="admin-bulk-delete-cancel">
                      Keep them
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="admin-bulk-delete-confirm"
                      onClick={bulkDelete}
                      className="bg-[#D63031] hover:bg-[#b82828]"
                    >
                      Yes, delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <button
                data-testid="admin-bulk-clear"
                onClick={clearSelection}
                className="text-xs underline text-white/85 hover:text-white"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 bg-white border border-[#F39C12]/20 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-[#F39C12]">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-10 text-center text-[#7F8C8D]">
              {orders.length === 0 ? "No orders yet." : "No orders match this filter."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="min-w-full text-sm"
                data-testid="admin-orders-table"
              >
                <thead className="bg-[#FFF5E1] text-[#2C3E50]">
                  <tr>
                    <th className="w-10 pl-4">
                      <Checkbox
                        data-testid="admin-select-all"
                        checked={allFilteredSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Select all rows"
                      />
                    </th>
                    <th className="w-8"></th>
                    <th className="text-left px-4 py-3 font-semibold">Order #</th>
                    <th className="text-left px-4 py-3 font-semibold">Date</th>
                    <th className="text-left px-4 py-3 font-semibold">Customer</th>
                    <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                    <th className="text-left px-4 py-3 font-semibold">City</th>
                    <th className="text-right px-4 py-3 font-semibold">Total</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-left px-4 py-3 font-semibold">Update</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const isOpen = expandedId === o.order_number;
                    const isSelected = selectedIds.has(o.order_number);
                    return (
                    <React.Fragment key={o.id}>
                    <tr
                      data-testid={`admin-row-${o.order_number}`}
                      className={`border-t border-[#F39C12]/15 hover:bg-[#FFFBF5] cursor-pointer ${
                        isSelected ? "bg-[#FFF5E1]/60" : ""
                      }`}
                      onClick={() =>
                        setExpandedId(isOpen ? null : o.order_number)
                      }
                    >
                      <td
                        className="pl-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          data-testid={`admin-select-${o.order_number}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(o.order_number)}
                          aria-label={`Select order ${o.order_number}`}
                        />
                      </td>
                      <td className="px-2 py-3 text-[#7F8C8D]">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </td>
                      <td className="px-4 py-3 font-display font-semibold">
                        {o.order_number}
                      </td>
                      <td className="px-4 py-3 text-[#7F8C8D]">
                        {new Date(o.created_at).toLocaleString("en-IN", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </td>
                      <td className="px-4 py-3">{o.shipping?.name}</td>
                      <td className="px-4 py-3">{o.shipping?.mobile}</td>
                      <td className="px-4 py-3">
                        {o.shipping?.city}, {o.shipping?.state}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        ₹{(o.total || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
                            STATUS_COLORS[o.status] || STATUS_COLORS.placed
                          }`}
                        >
                          {o.status}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Select
                          value={o.status}
                          onValueChange={(v) => updateStatus(o.order_number, v)}
                          disabled={updatingId === o.order_number}
                        >
                          <SelectTrigger
                            data-testid={`status-select-${o.order_number}`}
                            className="h-9 w-36 bg-white border-[#F39C12]/30"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr
                        data-testid={`admin-detail-${o.order_number}`}
                        className="bg-[#FFFBF5]/70 border-t border-[#F39C12]/10"
                      >
                        <td colSpan="10" className="px-6 py-5">
                          <div className="grid md:grid-cols-3 gap-5 text-sm">
                            <div>
                              <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold mb-2">
                                Full shipping address
                              </p>
                              <p className="font-semibold">{o.shipping?.name}</p>
                              <p className="text-[#2C3E50]/85 mt-1 whitespace-pre-line">
                                {o.shipping?.address_line_1}
                                {o.shipping?.address_line_2
                                  ? `\n${o.shipping.address_line_2}`
                                  : ""}
                                {"\n"}
                                {o.shipping?.city}, {o.shipping?.state} -{" "}
                                {o.shipping?.pincode}
                                {o.shipping?.landmark
                                  ? `\nLandmark: ${o.shipping.landmark}`
                                  : ""}
                              </p>
                              <p className="mt-3 text-[#7F8C8D]">
                                Mobile: {o.shipping?.mobile}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold mb-2">
                                Items
                              </p>
                              <ul className="space-y-1.5">
                                {o.items?.map((it, i) => (
                                  <li
                                    key={i}
                                    className="flex justify-between"
                                  >
                                    <span>
                                      {it.name}{" "}
                                      <span className="text-[#7F8C8D]">
                                        × {it.quantity}
                                      </span>
                                    </span>
                                    <span className="font-semibold">
                                      ₹{(it.price * it.quantity).toLocaleString("en-IN")}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                              <div className="mt-3 border-t border-[#F39C12]/15 pt-2 space-y-1 text-xs">
                                <div className="flex justify-between text-[#7F8C8D]">
                                  <span>Subtotal</span>
                                  <span>₹{(o.subtotal || 0).toLocaleString("en-IN")}</span>
                                </div>
                                <div className="flex justify-between text-[#27AE60]">
                                  <span>Shipping</span>
                                  <span>
                                    ₹{(o.shipping_fee || 0).toLocaleString("en-IN")}
                                  </span>
                                </div>
                                <div className="flex justify-between font-semibold text-[#D63031] text-sm">
                                  <span>Total</span>
                                  <span>
                                    ₹{(o.total || 0).toLocaleString("en-IN")}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold mb-2">
                                Order info
                              </p>
                              <ul className="space-y-1.5 text-[#2C3E50]/90">
                                <li>
                                  <span className="text-[#7F8C8D]">Order ID:</span>{" "}
                                  <span className="font-mono text-xs break-all">
                                    {o.id}
                                  </span>
                                </li>
                                <li>
                                  <span className="text-[#7F8C8D]">Payment:</span>{" "}
                                  {o.payment_method || "COD"}
                                </li>
                                <li>
                                  <span className="text-[#7F8C8D]">Placed at:</span>{" "}
                                  {new Date(o.created_at).toLocaleString("en-IN")}
                                </li>
                                {o.notes && (
                                  <li className="pt-2">
                                    <span className="text-[#7F8C8D] block">
                                      Notes / delivery instructions:
                                    </span>
                                    <p className="mt-1 p-2 bg-white border border-[#F39C12]/20 rounded text-sm">
                                      {o.notes}
                                    </p>
                                  </li>
                                )}
                                <li className="pt-2 flex flex-wrap gap-2">
                                  <a
                                    href={`tel:${o.shipping?.mobile}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F39C12]/15 text-[#D63031] text-xs font-semibold"
                                  >
                                    Call
                                  </a>
                                  <a
                                    href={`https://wa.me/91${(o.shipping?.mobile || "").replace(/\D/g, "").slice(-10)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#25D366]/15 text-[#25D366] text-xs font-semibold"
                                  >
                                    WhatsApp
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
